<template>
  <div class="flex items-center space-x-2 whitespace-nowrap">
    <template v-if="showRunButton">
      <run-scorecard-button
        v-if="canRunScoreCard && !isManual"
        :model="model"
        for-table
        :is-group="modelIsScorecardGroup(model)"
      />
      <scorecard-edit-manual
        v-else-if="canUpdateScoreCard && !modelIsScorecardGroup(model)"
        :model="model"
      />
    </template>

    <span
      v-tooltip="modelValue"
      class="max-w-63 overflow-x-hidden text-ellipsis"
      :class="{
        'cursor-pointer': !pageIsView,
      }"
      data-cy="scorecard-item-name"
      @click="!pageIsView && openPreviewModal()"
    >
      {{ modelValue }}
    </span>
    <template v-if="showBadge">
      <lf-tag v-if="isManual">
        {{ $t("SCORECARDS.MANUAL") }}
      </lf-tag>
    </template>

    <scorecard-preview-modal
      v-if="!pageIsView && showPreviewModal && !isManual"
      :model="model"
      :started="!!model?.results?.length"
      :close="closePreviewModal"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";

import { Ability, PermissionSubject } from "@/enums/auth";
import { SCORECARD_TYPE_MANUAL } from "@/helpers/constants";
import { modelIsScorecardGroup } from "@/helpers/scorecards";
import { ScorecardsIsViewKey } from "@/helpers/symbols";
import { usePermissions } from "@/hooks/auth";
import { useDeals } from "@/hooks/deals";
import { useActiveStage } from "@/hooks/workflow";

import RunScorecardButton from "./RunScorecardButton.vue";
import ScorecardEditManual from "./ScorecardEditManual.vue";
import ScorecardPreviewModal from "./ScorecardPreviewModal.vue";
import LfTag from "@/components/ui/text/LfTag.vue";

import type { IScorecard, IScorecardGroup } from "@/models/scorecards";
import type { PropType } from "vue";

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
  model: {
    type: Object as PropType<IScorecard | IScorecardGroup>,
    required: true,
  },
  value: {
    type: String,
  },
  showBadge: {
    type: Boolean,
    default: true,
  },
  showRunButton: {
    type: Boolean,
    default: false,
  },
  scoreCardGroup: {
    type: Boolean,
    default: false,
  },
});

const { canPerformActionReactive } = usePermissions();
const { activeStage } = useActiveStage();
const { activeDeal } = useDeals();

const isManual = computed(() => {
  return props.model?.type === SCORECARD_TYPE_MANUAL;
});

const canRunScoreCard = isManual.value
  ? false
  : canPerformActionReactive(
      PermissionSubject[props.scoreCardGroup ? "scoreCardGroup" : "scoreCard"],
      Ability.run,
      {
        [props.scoreCardGroup ? "scoreCardGroup" : "scoreCard"]:
          props.model?.id,
        scoreCardStageId: activeStage.value?.id,
        application: activeDeal.value.id,
      }
    );

const canUpdateScoreCard = isManual.value
  ? canPerformActionReactive(
      PermissionSubject[props.scoreCardGroup ? "scoreCardGroup" : "scoreCard"],
      Ability.update,
      {
        [props.scoreCardGroup ? "scoreCardGroup" : "scoreCard"]:
          props.model?.id,
      }
    )
  : false;

const pageIsView = inject(ScorecardsIsViewKey, true);
const showPreviewModal = ref(false);

const openPreviewModal = () => {
  showPreviewModal.value = true;
};

const closePreviewModal = () => {
  showPreviewModal.value = false;
};
</script>
